import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import {
  OC, FONT, CATEGORY, CAT_KEYS, LOCATIONS, JOB_CATEGORIES, EMPLOYMENT_TYPES, WORKPLACE_TYPES, iconBtn,
} from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import {
  Button, Badge, Icon, Avatar, Card, MonoTag, Deadline, MetaChip, Input, Toggle, CategoryIcon,
} from '../components/ui.js';
import { LoadingState, ErrorState, EmptyState } from '../components/AsyncState.js';

// --- HomeScreen ---
function HomeScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const latest = (s.opportunities || []).slice(0, 6);
    const [news, setNews] = useState('');
    const [testimonials, setTestimonials] = useState([]);

    useEffect(() => {
        s.loadOpportunities({ limit: 6, sort: 'newest' });
        OCApi.getCommunityTestimonials().then((res) => setTestimonials(res.testimonials || [])).catch(() => {});
    }, []);

    const textColor = s.theme === 'dark' ? '#FFFFFF' : '#111827';
    const textSecondary = s.theme === 'dark' ? '#E5E7EB' : '#6B7280';
    const textMuted = s.theme === 'dark' ? '#9CA3AF' : '#6B7280';

    return React.createElement('div', { style: { paddingBottom: 80 } },
        React.createElement('div', { 
            style: { 
                backgroundImage: 'url(first.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: s.theme === 'dark' ? OC.charcoal : '#FFFFFF',
                borderBottom: `1px solid ${t.border.base}`,
                minHeight: '450px',
                position: 'relative',
            }
        },
            React.createElement('div', {
                style: {
                    position: 'absolute', inset: 0,
                    background: s.theme === 'dark'
                        ? 'linear-gradient(90deg, rgba(17,17,18,.96) 0%, rgba(17,17,18,.88) 42%, rgba(17,17,18,.4) 70%, rgba(17,17,18,0) 90%)'
                        : 'linear-gradient(90deg, rgba(255,255,255,.9) 0%, rgba(255,255,255,.5) 55%, rgba(255,255,255,.1) 100%)',
                }
            }),
            React.createElement('div', { 
                style: { 
                    maxWidth: 1180, 
                    margin: '0 auto', 
                    padding: '0 32px', 
                    width: '100%',
                    position: 'relative',
                }
            },
                React.createElement('div', { style: { padding: '72px 0 64px', maxWidth: 560 } },
                    React.createElement(Badge, { color: OC.orange, theme: s.theme, style: { fontSize: 12.5, padding: '6px 12px' } }, 'Sharing is caring'),
                    React.createElement('h1', { style: { 
                        fontFamily: FONT.display, 
                        fontWeight: 800, 
                        fontSize: 52, 
                        lineHeight: 1.05, 
                        letterSpacing: '-0.03em', 
                        color: textColor,
                        margin: '20px 0 16px' 
                    } },
                        'Every opportunity,', React.createElement('br', null), 'in one circle.'
                    ),
                    React.createElement('p', { style: { 
                        fontFamily: FONT.body, 
                        fontSize: 18, 
                        lineHeight: 1.55, 
                        color: textSecondary,
                        margin: '0 0 28px' 
                    } },
                        'Jobs, scholarships, internships, admissions and events — gathered, verified and shared so no one misses out for lack of access.'
                    ),
                    React.createElement('div', { style: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' } },
                        React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, onClick: () => s.go('feed'), iconRight: React.createElement(Icon, { name: 'arrowRight', size: 16 }) }, 'Browse opportunities'),
                        React.createElement(Button, { kind: 'secondary', size: 'lg', theme: s.theme, onClick: () => s.go('share'), icon: React.createElement(Icon, { name: 'plus', size: 16 }) }, 'Share one')
                    ),
                    React.createElement('div', { style: { display: 'flex', gap: 28, marginTop: 36 } },
                        [['18k+', 'live opportunities'], ['540', 'volunteer reviewers'], ['96', 'countries reached']].map(([n, l]) =>
                            React.createElement('div', { key: l },
                                React.createElement('div', { style: { 
                                    fontFamily: FONT.display, 
                                    fontWeight: 800, 
                                    fontSize: 26, 
                                    color: textColor,
                                } }, n),
                                React.createElement('div', { style: { 
                                    fontFamily: FONT.body, 
                                    fontSize: 13, 
                                    color: textMuted,
                                } }, l)
                            )
                        )
                    )
                )
            )
        ),
        React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '0 32px', width: '100%' } },
            React.createElement('div', { style: { padding: '48px 0 8px' } },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 } },
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: OC.orange, marginBottom: 8 } }, 'Explore by type'),
                        React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, margin: 0, color: t.text.primary, letterSpacing: '-0.02em' } }, 'What are you looking for?')
                    )
                ),
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 } },
                    CAT_KEYS.map(key => {
                        const cat = CATEGORY[key];
                        return React.createElement('button', {
                            key: key, onClick: () => s.go('feed', { cat: key }),
                            style: {
                                background: t.bg.card, border: `1px solid ${t.border.base}`,
                                borderRadius: 16, padding: 20, cursor: 'pointer',
                                textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12,
                                fontFamily: FONT.body, transition: 'transform .12s ease',
                            },
                            onMouseEnter: e => e.currentTarget.style.transform = 'translateY(-3px)',
                            onMouseLeave: e => e.currentTarget.style.transform = 'none',
                        },
                            React.createElement(CategoryIcon, { category: key, size: 42, theme: s.theme }),
                            React.createElement('div', null,
                                React.createElement('div', { style: { fontWeight: 700, fontSize: 15, color: t.text.primary } }, cat.label),
                                React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 2 } }, 'Live now')
                            )
                        );
                    })
                )
            ),
            React.createElement('div', { style: { padding: '44px 0 8px' } },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 } },
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: OC.orange, marginBottom: 8 } }, 'Fresh today'),
                        React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, margin: 0, color: t.text.primary, letterSpacing: '-0.02em' } }, 'Latest opportunities')
                    ),
                    React.createElement('button', {
                        onClick: () => s.go('feed'),
                        style: { background: 'none', border: 'none', cursor: 'pointer', color: OC.orange, fontFamily: FONT.body, fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }
                    }, 'See all', React.createElement(Icon, { name: 'arrowRight', size: 15 }))
                ),
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 } },
                    latest.map(op => React.createElement(OpportunityCard, { key: op.id, op: op }))
                )
            ),
            testimonials.length ? React.createElement('div', { style: { padding: '52px 0 8px' } },
                React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: OC.orange, marginBottom: 8 } }, 'Voices from the circle'),
                React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, margin: '0 0 20px', color: t.text.primary } }, 'Real people. Real breaks.'),
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 } },
                    testimonials.slice(0, 3).map((tm) =>
                        React.createElement('div', {
                            key: tm.id,
                            style: {
                                background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 16, padding: 22,
                            }
                        },
                            React.createElement('p', { style: { fontSize: 15, lineHeight: 1.55, color: t.text.primary, margin: '0 0 16px', fontStyle: 'italic' } }, `"${tm.quote}"`),
                            React.createElement('div', { style: { fontWeight: 700, fontSize: 14, color: t.text.primary } }, tm.personName),
                            tm.roleLabel ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 2 } }, tm.roleLabel) : null
                        )
                    )
                )
            ) : null,
            React.createElement('div', {
                style: {
                    margin: '52px 0 0', background: OC.charcoal,
                    borderRadius: 22, padding: '44px 48px',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 32, flexWrap: 'wrap',
                }
            },
                React.createElement('div', { style: { maxWidth: 440 } },
                    React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' } },
                        'Never miss a deadline'
                    ),
                    React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 15, color: 'rgba(255,255,255,.7)', margin: 0, lineHeight: 1.5 } },
                        'One weekly digest of opportunities matched to your interests. No spam, unsubscribe anytime.'
                    )
                ),
                React.createElement('div', { style: { display: 'flex', gap: 10, flex: '1 1 320px', maxWidth: 440 } },
                    React.createElement('input', {
                        value: news,
                        onChange: e => setNews(e.target.value),
                        placeholder: 'you@email.com',
                        style: {
                            flex: 1, height: 48, borderRadius: 11, border: 'none',
                            padding: '0 16px', fontFamily: FONT.body, fontSize: 15,
                            background: 'rgba(255,255,255,.12)', color: '#fff',
                        }
                    }),
                    React.createElement(Button, {
                        kind: 'primary', size: 'lg', theme: 'dark',
                        onClick: async () => {
                            if (!news.trim()) return;
                            try {
                                await OCApi.subscribeNewsletter(news.trim(), 'home');
                                s.showToast('Subscribed — check your inbox', 'mail');
                                setNews('');
                            } catch (err) {
                                s.showToast(err.message || 'Could not subscribe', 'x');
                            }
                        }
                    }, 'Subscribe')
                )
            )
        )
    );
}

function locBucket(op) {
    const l = (op.location || '').toLowerCase();
    if (l.includes('remote')) return 'remote';
    if (l.includes('hybrid')) return 'hybrid';
    return 'onsite';
}

function groupsForCat(cat) {
    const commonGroups = [
        { key: 'loc', title: 'Location', test: locBucket, opts: [['remote', 'Remote'], ['hybrid', 'Hybrid'], ['onsite', 'On-site']] },
    ];
    const catGroups = {
        all: [],
        jobs: [],
        internships: [],
        scholarships: [],
        admissions: [],
        events: [],
    };
    return [...commonGroups, ...(catGroups[cat] || catGroups.all)];
}

// --- FeedScreen ---
function readFeedFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return {
        cat: params.get('category') || 'all',
        sort: params.get('sort') || 'deadline',
        searchQuery: params.get('q') || '',
        selectedLocation: params.get('city') || 'all',
        remoteOnly: params.get('is_remote') === 'true',
        closingWeek: params.get('closing_week') === 'true',
    };
}

function syncFeedFiltersToUrl({ cat, sort, searchQuery, selectedLocation, remoteOnly, closingWeek }) {
    const params = new URLSearchParams();
    if (cat && cat !== 'all') params.set('category', cat);
    if (sort && sort !== 'deadline') params.set('sort', sort);
    if (searchQuery) params.set('q', searchQuery);
    if (selectedLocation && selectedLocation !== 'all') params.set('city', selectedLocation);
    if (remoteOnly) params.set('is_remote', 'true');
    if (closingWeek) params.set('closing_week', 'true');
    const qs = params.toString();
    window.history.replaceState({}, '', qs ? `/feed?${qs}` : '/feed');
}

function FeedScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const initialFilters = readFeedFiltersFromUrl();
    const [cat, setCat] = useState(s.route.cat || initialFilters.cat || 'all');
    const [sort, setSort] = useState(initialFilters.sort || 'deadline');
    const [savedOnly, setSavedOnly] = useState(false);
    const [view, setView] = useState('grid');
    const [filters, setFilters] = useState({});
    
    const [searchQuery, setSearchQuery] = useState(initialFilters.searchQuery || '');
    const [selectedLocation, setSelectedLocation] = useState(initialFilters.selectedLocation || 'all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [workplaceTypes, setWorkplaceTypes] = useState(initialFilters.remoteOnly ? ['Remote'] : []);
    const [salaryRange, setSalaryRange] = useState(75000);
    const [closingWeek, setClosingWeek] = useState(initialFilters.closingWeek || false);

    const buildFeedApiParams = () => {
        const params = {
            limit: 50,
            sort: sort === 'deadline' ? 'deadline' : (sort === 'relevance' && searchQuery.trim() ? 'relevance' : 'newest'),
        };
        if (cat !== 'all') params.category = cat;
        if (searchQuery.trim()) params.q = searchQuery.trim();
        if (selectedLocation !== 'all') params.city = selectedLocation;
        if (workplaceTypes.includes('Remote')) params.is_remote = true;
        if (closingWeek) {
            const after = new Date();
            const before = new Date();
            before.setDate(before.getDate() + 7);
            params.deadline_after = after.toISOString();
            params.deadline_before = before.toISOString();
        }
        return params;
    };

    useEffect(() => {
        if (s.route.name !== 'feed') return;
        syncFeedFiltersToUrl({
            cat,
            sort,
            searchQuery,
            selectedLocation,
            remoteOnly: workplaceTypes.includes('Remote'),
            closingWeek,
        });
    }, [s.route.name, cat, sort, searchQuery, selectedLocation, workplaceTypes, closingWeek]);

    useEffect(() => {
        if (s.route.name !== 'feed') return;
        const params = buildFeedApiParams();
        const hasCached = (s.opportunities?.length ?? 0) > 0;
        s.loadOpportunities(params, { background: hasCached });
    }, [s.route.name, cat, sort, searchQuery, selectedLocation, workplaceTypes, closingWeek]);

    const toggleEmployment = (type) => {
        setEmploymentTypes(prev => 
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };
    const toggleWorkplace = (type) => {
        setWorkplaceTypes(prev => 
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const visibleGroups = groupsForCat(cat);
    const changeCat = (next) => { setCat(next); setFilters({}); };
    const toggleFilter = (group, val) => setFilters(f => {
        const cur = f[group] || [];
        return { ...f, [group]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
    const resetFilters = () => {
        setFilters({});
        setSavedOnly(false);
        setCat('all');
        setSearchQuery('');
        setSelectedLocation('all');
        setSelectedCategory('all');
        setEmploymentTypes([]);
        setWorkplaceTypes([]);
        setSalaryRange(75000);
        setClosingWeek(false);
    };
    const activeCount = visibleGroups.reduce((n, g) => n + (filters[g.key] || []).length, 0) + (savedOnly ? 1 : 0) + (cat !== 'all' ? 1 : 0);
    const allOpps = s.opportunities || [];
    let list = allOpps.filter(o => {
        if (searchQuery && !o.title.toLowerCase().includes(searchQuery.toLowerCase()) && !o.org.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }
        if (selectedCategory !== 'all' && o.category !== selectedCategory.toLowerCase()) {
            return false;
        }
        if (selectedLocation !== 'all') {
            const locMatch = o.location.toLowerCase().includes(selectedLocation.toLowerCase());
            if (!locMatch) return false;
        }
        if (employmentTypes.length > 0) {
            const empMatch = employmentTypes.some(et => (o.type || '').toLowerCase().includes(et.toLowerCase()));
            if (!empMatch) return false;
        }
        if (workplaceTypes.length > 0) {
            const wpMatch = workplaceTypes.some(wp => o.location.toLowerCase().includes(wp.toLowerCase()));
            if (!wpMatch) return false;
        }
        if (cat !== 'all' && o.category !== cat) return false;
        if (savedOnly && !s.isSaved(o.id)) return false;
        for (const g of visibleGroups) {
            const sel = filters[g.key] || [];
            if (sel.length && !sel.includes(g.test(o))) return false;
        }
        return true;
    });
    list = [...list].sort((a, b) =>
        sort === 'deadline' ? (a.daysLeft ?? 999) - (b.daysLeft ?? 999) :
        sort === 'newest' ? (b.daysLeft ?? 999) - (a.daysLeft ?? 999) :
        a.title.localeCompare(b.title)
    );

    const tabs = [{ key: 'all', label: 'All' }, ...CAT_KEYS.map(k => ({ key: k, label: CATEGORY[k].label }))];

    const chips = [];
    if (cat !== 'all') chips.push({ label: CATEGORY[cat].label, onX: () => changeCat('all') });
    if (searchQuery) chips.push({ label: `Search: ${searchQuery}`, onX: () => setSearchQuery('') });
    if (selectedLocation !== 'all') chips.push({ label: `Location: ${selectedLocation}`, onX: () => setSelectedLocation('all') });
    if (selectedCategory !== 'all') chips.push({ label: `Category: ${selectedCategory}`, onX: () => setSelectedCategory('all') });
    employmentTypes.forEach(et => chips.push({ label: et, onX: () => toggleEmployment(et) }));
    workplaceTypes.forEach(wt => chips.push({ label: wt, onX: () => toggleWorkplace(wt) }));
    visibleGroups.forEach(g => g.opts.forEach(([v, lbl]) => {
        if ((filters[g.key] || []).includes(v)) chips.push({ label: lbl, onX: () => toggleFilter(g.key, v) });
    }));
    if (closingWeek) chips.push({ label: 'Closing this week', onX: () => setClosingWeek(false) });
    if (savedOnly) chips.push({ label: 'Saved only', onX: () => setSavedOnly(false) });

    const buildFeedFilters = () => {
        const filters = {};
        if (cat !== 'all') filters.category = cat;
        if (searchQuery.trim()) filters.q = searchQuery.trim();
        if (workplaceTypes.includes('Remote')) filters.isRemote = true;
        if (selectedLocation !== 'all') filters.city = selectedLocation;
        if (closingWeek) {
            const after = new Date();
            const before = new Date();
            before.setDate(before.getDate() + 7);
            filters.deadline_after = after.toISOString();
            filters.deadline_before = before.toISOString();
        }
        return filters;
    };

    const saveCurrentFeedSearch = () => {
        const filters = buildFeedFilters();
        const labelParts = [];
        if (filters.category) labelParts.push(CATEGORY[filters.category]?.label || filters.category);
        if (filters.q) labelParts.push(`"${filters.q}"`);
        if (filters.isRemote) labelParts.push('Remote');
        if (filters.city) labelParts.push(filters.city);
        s.saveSearch({
            name: labelParts.length ? labelParts.join(' · ') : 'All opportunities',
            filters,
            alertEnabled: true,
        });
    };

    return React.createElement('div', { style: { padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '36px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text.secondary, marginBottom: 12 } },
                React.createElement('button', { onClick: () => s.go('home'), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, fontSize: 12.5, padding: 0 } }, 'Home'),
                React.createElement(Icon, { name: 'chevRight', size: 12 }),
                React.createElement('span', { style: { color: t.text.primary, fontWeight: 600 } }, 'Opportunities')
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 22, gap: 24, flexWrap: 'wrap' } },
                React.createElement('div', null,
                    React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 36, margin: 0, color: t.text.primary, letterSpacing: '-0.025em' } },
                        'Browse all opportunities'
                    ),
                    React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 15, color: t.text.secondary, margin: '6px 0 0' } },
                        React.createElement('strong', { style: { color: t.text.primary } }, list.length),
                        s.oppRefreshing ? ' updating…' : (s.oppLoading ? ' loading…' : ' matching · refreshed moments ago')
                    )
                ),
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
                    React.createElement('div', { style: { display: 'flex', gap: 4, padding: 4, background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 12 } },
                        [['grid', 'Grid'], ['list', 'List']].map(([v, lbl]) =>
                            React.createElement('button', {
                                key: v, onClick: () => setView(v),
                                style: {
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '7px 13px', borderRadius: 9, cursor: 'pointer', border: 'none',
                                    background: view === v ? OC.orange : 'transparent',
                                    color: view === v ? '#fff' : t.text.secondary,
                                    fontFamily: FONT.body, fontSize: 13, fontWeight: 600,
                                }
                            }, React.createElement(Icon, { name: v, size: 15 }), lbl)
                        )
                    ),
                    React.createElement(Button, {
                        kind: 'secondary', size: 'md', theme: s.theme,
                        icon: React.createElement(Icon, { name: 'bell', size: 15 }),
                        onClick: saveCurrentFeedSearch,
                    }, 'Save search')
                )
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 } },
                tabs.map(tb => {
                    const active = cat === tb.key;
                    return React.createElement('button', {
                        key: tb.key, onClick: () => changeCat(tb.key),
                        style: {
                            border: `1px solid ${active ? OC.orange : t.border.base}`,
                            background: active ? OC.orange : t.bg.card,
                            color: active ? '#fff' : t.text.secondary,
                            borderRadius: 999, padding: '9px 16px',
                            cursor: 'pointer', fontFamily: FONT.body,
                            fontWeight: 600, fontSize: 13.5,
                        }
                    }, tb.label);
                })
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 28, alignItems: 'start' } },
                React.createElement('aside', {
                    style: {
                        background: t.bg.card,
                        border: `1px solid ${t.border.base}`,
                        borderRadius: 16,
                        padding: 20,
                        position: 'sticky',
                        top: 88,
                        maxHeight: 'calc(100vh - 120px)',
                        overflowY: 'auto',
                    }
                },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 } },
                        React.createElement('span', { style: { fontFamily: FONT.display, fontSize: 15, fontWeight: 700, color: t.text.primary } }, 'Filters'),
                        (activeCount || searchQuery || selectedLocation !== 'all' || selectedCategory !== 'all' || employmentTypes.length > 0 || workplaceTypes.length > 0) ?
                            React.createElement('button', { onClick: resetFilters, style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: OC.orange, fontWeight: 600 } }, 'Reset All')
                            : null
                    ),
                    
                    // Search Input
                    React.createElement('div', { style: { marginBottom: 16 } },
                        React.createElement('div', { style: { 
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: t.bg.page, border: `1px solid ${t.border.base}`,
                            borderRadius: 9, padding: '0 12px',
                        } },
                            React.createElement(Icon, { name: 'search', size: 16, color: t.text.secondary }),
                            React.createElement('input', {
                                value: searchQuery,
                                onChange: e => setSearchQuery(e.target.value),
                                placeholder: 'Search by title...',
                                style: {
                                    flex: 1, border: 'none', outline: 'none',
                                    background: 'transparent', fontFamily: FONT.body,
                                    fontSize: 13.5, color: t.text.primary,
                                    padding: '9px 0',
                                }
                            }),
                            searchQuery ? React.createElement('button', {
                                onClick: () => setSearchQuery(''),
                                style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary }
                            }, React.createElement(Icon, { name: 'x', size: 14 })) : null
                        )
                    ),

                    // Saved only
                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 } },
                        React.createElement('label', {
                            style: { display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', cursor: 'pointer' },
                            onClick: () => setSavedOnly(v => !v)
                        },
                            React.createElement('span', {
                                style: {
                                    width: 17, height: 17, borderRadius: 5, flex: '0 0 auto',
                                    background: savedOnly ? OC.orange : 'transparent',
                                    border: `1.5px solid ${savedOnly ? OC.orange : t.border.strong}`,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }
                            }, savedOnly ? React.createElement(Icon, { name: 'check', size: 11, color: '#fff' }) : null),
                            React.createElement('span', { style: { fontSize: 13.5, color: t.text.primary, fontWeight: 500, flex: 1 } }, 'Saved only'),
                            React.createElement('span', { style: { fontSize: 11.5, color: t.text.secondary } }, s.saved.length)
                        )
                    ),

                    // Location Dropdown
                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 8 } }, '📍 Location'),
                        React.createElement('select', {
                            value: selectedLocation,
                            onChange: e => setSelectedLocation(e.target.value),
                            style: {
                                width: '100%', padding: '8px 12px', borderRadius: 8,
                                background: t.bg.page, border: `1px solid ${t.border.base}`,
                                fontFamily: FONT.body, fontSize: 13, color: t.text.primary,
                                outline: 'none', cursor: 'pointer',
                            }
                        },
                            React.createElement('option', { value: 'all' }, 'All Locations'),
                            LOCATIONS.map(loc => 
                                React.createElement('option', { key: loc, value: loc }, loc)
                            )
                        )
                    ),

                    // Job Category Dropdown
                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 8 } }, '🏷️ Job Category'),
                        React.createElement('select', {
                            value: selectedCategory,
                            onChange: e => setSelectedCategory(e.target.value),
                            style: {
                                width: '100%', padding: '8px 12px', borderRadius: 8,
                                background: t.bg.page, border: `1px solid ${t.border.base}`,
                                fontFamily: FONT.body, fontSize: 13, color: t.text.primary,
                                outline: 'none', cursor: 'pointer',
                            }
                        },
                            React.createElement('option', { value: 'all' }, 'All Categories'),
                            JOB_CATEGORIES.map(cat => 
                                React.createElement('option', { key: cat, value: cat }, cat)
                            )
                        )
                    ),

                    // Employment Type Checkboxes
                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 8 } }, '💼 Employment Type'),
                        EMPLOYMENT_TYPES.map(et => {
                            const count = allOpps.filter(o => o.type && o.type.toLowerCase().includes(et.toLowerCase())).length;
                            const on = employmentTypes.includes(et);
                            return React.createElement('label', {
                                key: et, onClick: () => toggleEmployment(et),
                                style: { display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', cursor: 'pointer' }
                            },
                                React.createElement('span', {
                                    style: {
                                        width: 17, height: 17, borderRadius: 5, flex: '0 0 auto',
                                        background: on ? OC.orange : 'transparent',
                                        border: `1.5px solid ${on ? OC.orange : t.border.strong}`,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }
                                }, on ? React.createElement(Icon, { name: 'check', size: 11, color: '#fff' }) : null),
                                React.createElement('span', { style: { fontSize: 13, color: t.text.primary, fontWeight: 500, flex: 1 } }, et),
                                React.createElement('span', { style: { fontSize: 11.5, color: t.text.secondary } }, count)
                            );
                        })
                    ),

                    // Workplace Type Checkboxes
                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 8 } }, '🏠 Workplace Type'),
                        WORKPLACE_TYPES.map(wt => {
                            const count = allOpps.filter(o => o.location && o.location.toLowerCase().includes(wt.toLowerCase())).length;
                            const on = workplaceTypes.includes(wt);
                            return React.createElement('label', {
                                key: wt, onClick: () => toggleWorkplace(wt),
                                style: { display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', cursor: 'pointer' }
                            },
                                React.createElement('span', {
                                    style: {
                                        width: 17, height: 17, borderRadius: 5, flex: '0 0 auto',
                                        background: on ? OC.orange : 'transparent',
                                        border: `1.5px solid ${on ? OC.orange : t.border.strong}`,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }
                                }, on ? React.createElement(Icon, { name: 'check', size: 11, color: '#fff' }) : null),
                                React.createElement('span', { style: { fontSize: 13, color: t.text.primary, fontWeight: 500, flex: 1 } }, wt),
                                React.createElement('span', { style: { fontSize: 11.5, color: t.text.secondary } }, count)
                            );
                        })
                    ),

                    // Salary Range
                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.text.secondary, marginBottom: 8 } }, '💰 Salary Range'),
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                            React.createElement('span', { style: { fontSize: 12, color: t.text.secondary } }, '$20K'),
                            React.createElement('input', {
                                type: 'range',
                                min: 20000,
                                max: 150000,
                                step: 10000,
                                value: salaryRange,
                                onChange: e => setSalaryRange(parseInt(e.target.value)),
                                style: { flex: 1, accentColor: OC.orange }
                            }),
                            React.createElement('span', { style: { fontSize: 12, color: t.text.primary, fontWeight: 600 } }, 
                                salaryRange >= 150000 ? '$150K+' : `$${salaryRange/1000}K`
                            )
                        )
                    ),

                    React.createElement('div', { style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                        React.createElement('label', {
                            onClick: () => setClosingWeek(v => !v),
                            style: { display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', cursor: 'pointer' }
                        },
                            React.createElement('span', {
                                style: {
                                    width: 17, height: 17, borderRadius: 4, flex: '0 0 auto',
                                    background: closingWeek ? OC.orange : 'transparent',
                                    border: `1.5px solid ${closingWeek ? OC.orange : t.border.strong}`,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }
                            }, closingWeek ? React.createElement('span', { style: { width: 7, height: 7, borderRadius: 2, background: '#fff' } }) : null),
                            React.createElement('span', { style: { fontSize: 13, color: t.text.primary, fontWeight: 500 } }, 'Closing this week')
                        )
                    ),

                    // Category filters
                    cat !== 'all' ? React.createElement('div', {
                        style: {
                            display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, marginTop: 12, paddingTop: 12,
                            padding: '8px 10px', borderRadius: 9,
                            background: s.theme === 'dark' ? 'rgba(249,115,22,.12)' : OC.orangeMuted,
                            borderTop: `1px solid ${t.border.base}`,
                        }
                    },
                        React.createElement(CategoryIcon, { category: cat, size: 26, theme: s.theme }),
                        React.createElement('span', { style: { fontSize: 12.5, fontWeight: 600, color: OC.orange } }, CATEGORY[cat].label, ' filters')
                    ) : null,
                    visibleGroups.map(g =>
                        React.createElement('div', { key: g.key, style: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
                            React.createElement('div', {
                                style: {
                                    fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 600,
                                    letterSpacing: '0.14em', textTransform: 'uppercase',
                                    color: t.text.secondary, marginBottom: 10,
                                }
                            }, g.title),
                            g.opts.map(([v, lbl]) => {
                                const count = allOpps.filter(o => g.test(o) === v && (cat === 'all' || o.category === cat)).length;
                                const on = (filters[g.key] || []).includes(v);
                                return React.createElement('label', {
                                    key: v, onClick: () => toggleFilter(g.key, v),
                                    style: { display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', cursor: 'pointer' }
                                },
                                    React.createElement('span', {
                                        style: {
                                            width: 17, height: 17, borderRadius: 5, flex: '0 0 auto',
                                            background: on ? OC.orange : 'transparent',
                                            border: `1.5px solid ${on ? OC.orange : t.border.strong}`,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        }
                                    }, on ? React.createElement(Icon, { name: 'check', size: 11, color: '#fff' }) : null),
                                    React.createElement('span', { style: { fontSize: 13, color: t.text.primary, fontWeight: 500, flex: 1 } }, lbl),
                                    React.createElement('span', { style: { fontSize: 11.5, color: t.text.secondary } }, count)
                                );
                            })
                        )
                    )
                ),
                // Results
                React.createElement('div', null,
                    // Sort + active chips bar
                    React.createElement('div', {
                        style: {
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            gap: 12, padding: '12px 16px', marginBottom: 16,
                            background: t.bg.card, border: `1px solid ${t.border.base}`,
                            borderRadius: 12, flexWrap: 'wrap',
                        }
                    },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
                            chips.length ? React.createElement('span', { style: { fontSize: 12.5, color: t.text.secondary, fontWeight: 500 } }, 'Active:') :
                                React.createElement('span', { style: { fontSize: 12.5, color: t.text.secondary } }, 'No filters applied'),
                            chips.map((c, i) =>
                                React.createElement('button', {
                                    key: i, onClick: c.onX,
                                    style: {
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        padding: '6px 10px', borderRadius: 8,
                                        background: s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted,
                                        border: `1px solid ${OC.orangeSubtle}`, color: OC.orange,
                                        fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                    }
                                }, c.label, React.createElement(Icon, { name: 'x', size: 12 }))
                            )
                        ),
                        React.createElement('div', {
                            style: {
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                fontFamily: FONT.body, fontSize: 13, color: t.text.secondary,
                            }
                        },
                            React.createElement(Icon, { name: 'filter', size: 14 }),
                            React.createElement('select', {
                                value: sort, onChange: e => setSort(e.target.value),
                                style: {
                                    border: 'none', background: 'transparent',
                                    fontFamily: FONT.body, fontSize: 13,
                                    fontWeight: 600,
                                    color: s.theme === 'dark' ? '#F9FAFB' : '#111827',
                                    cursor: 'pointer', outline: 'none',
                                }
                            },
                                React.createElement('option', { value: 'deadline' }, 'Closing soonest'),
                                React.createElement('option', { value: 'newest' }, 'Latest deadline'),
                                React.createElement('option', { value: 'az' }, 'A–Z')
                            )
                        )
                    ),
                    (s.oppLoading && list.length === 0)
                        ? React.createElement(LoadingState, { message: 'Loading opportunities…', theme: s.theme })
                        : s.oppLoadError && list.length === 0
                            ? React.createElement(ErrorState, {
                                message: s.oppLoadError,
                                theme: s.theme,
                                onRetry: () => s.loadOpportunities(buildFeedApiParams(), { force: true, background: false }),
                            })
                            : list.length ? (
                        view === 'grid' ?
                            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 } },
                                list.map(op => React.createElement(OpportunityCard, { key: op.id, op: op }))
                            ) :
                            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
                                list.map(op => React.createElement(FeedRow, { key: op.id, op: op }))
                            )
                    ) : React.createElement('div', { style: { textAlign: 'center', padding: '64px 24px', border: `1px dashed ${t.border.strong}`, borderRadius: 18, background: t.bg.card } },
                        React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 19, color: t.text.primary, margin: '0 0 6px' } },
                            savedOnly ? 'Nothing saved matches' : 'No opportunities match your filters'
                        ),
                        React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 14.5, color: t.text.secondary, margin: '0 0 20px' } },
                            'Try removing a filter or widening your criteria.'
                        ),
                        React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: resetFilters }, 'Reset filters')
                    )
                )
            )
        )
    );
}

// --- FeedRow ---
function FeedRow({ op }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const cat = CATEGORY[op.category] || CATEGORY.jobs;
    const saved = s.isSaved(op.id);
    const applied = s.hasApplied(op.id);

    return React.createElement('article', {
        onClick: () => s.go('detail', { id: op.id }),
        style: {
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '14px 18px', borderRadius: 14,
            background: t.bg.card, border: `1px solid ${t.border.base}`,
            fontFamily: FONT.body, cursor: 'pointer',
        }
    },
        React.createElement(Avatar, { name: op.org, size: 44, theme: s.theme }),
        React.createElement('div', { style: { flex: 1, minWidth: 0 } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 } },
                React.createElement(Badge, { color: cat.color, theme: s.theme, icon: React.createElement(Icon, { name: cat.icon, size: 11 }) }, cat.label),
                React.createElement('span', { style: { fontSize: 11.5, color: t.text.secondary, fontFamily: FONT.mono } }, op.source)
            ),
            React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, margin: 0, color: t.text.primary, letterSpacing: '-0.012em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, op.title),
            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 2 } }, op.org, ' · ', op.location)
        ),
        React.createElement(Deadline, { days: op.daysLeft, theme: s.theme }),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
            React.createElement('button', {
                onClick: (e) => { e.stopPropagation(); s.toggleSave(op.id); },
                style: { ...iconBtn(t), color: saved ? OC.orange : t.text.secondary, borderColor: saved ? OC.orange : t.border.base }
            }, React.createElement(Icon, { name: saved ? 'bookmarkFilled' : 'bookmark', size: 15 })),
            React.createElement('button', {
                onClick: (e) => { e.stopPropagation(); s.startApply(op.id); },
                style: { ...iconBtn(t), background: applied ? 'transparent' : OC.orange, color: applied ? OC.success : '#fff', border: applied ? `1px solid ${OC.success}` : 'none', padding: '0 14px', width: 'auto', fontWeight: 600, fontSize: 13, gap: 4 }
            }, applied ? React.createElement(React.Fragment, null, React.createElement(Icon, { name: 'check', size: 13 }), ' Applied') : React.createElement(React.Fragment, null, 'Apply ', React.createElement(Icon, { name: 'arrowRight', size: 13 })))
        )
    );
}

// --- OpportunityCard ---
function OpportunityCard({ op }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const cat = CATEGORY[op.category] || CATEGORY.jobs;
    const saved = s.isSaved(op.id);
    const applied = s.hasApplied(op.id);
    const [hover, setHover] = useState(false);

    return React.createElement('article', {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
        onClick: () => s.go('detail', { id: op.id }),
        style: {
            background: t.bg.card, border: `1px solid ${hover ? t.border.strong : t.border.base}`,
            borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column',
            gap: 14, fontFamily: FONT.body, position: 'relative', cursor: 'pointer',
            boxShadow: hover ? t.shadow.md : 'none',
            transition: 'box-shadow .15s ease, border-color .15s ease',
            transform: hover ? 'translateY(-2px)' : 'none',
        }
    },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 } },
            React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
                React.createElement(Badge, { color: cat.color, theme: s.theme, icon: React.createElement(Icon, { name: cat.icon, size: 11 }) }, cat.label),
                op.isExpired ? React.createElement(Badge, { color: OC.neutral, theme: s.theme }, 'Expired') : null
            ),
            React.createElement(Deadline, { days: op.daysLeft, theme: s.theme })
        ),
        React.createElement('div', { style: { display: 'flex', gap: 12, alignItems: 'flex-start' } },
            React.createElement(Avatar, { name: op.org, size: 40, theme: s.theme }),
            React.createElement('div', { style: { minWidth: 0, flex: 1 } },
                React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 17, lineHeight: 1.25, margin: 0, color: t.text.primary, letterSpacing: '-0.012em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } }, op.title),
                React.createElement('div', { style: { fontSize: 13.5, color: t.text.secondary, marginTop: 4, fontWeight: 500 } }, op.org)
            )
        ),
        React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
            React.createElement(MetaChip, { theme: s.theme, icon: 'mapPin' }, op.location),
            op.salary && op.salary !== '—' ? React.createElement(MetaChip, { theme: s.theme, icon: 'sparkle' }, op.salary) : null,
            op.type && op.type !== 'On-site' ? React.createElement(MetaChip, { theme: s.theme, icon: 'briefcase' }, op.type) : null,
            op.level ? React.createElement(MetaChip, { theme: s.theme, icon: 'award' }, op.level) : null
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: `1px solid ${t.border.base}` } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: t.text.secondary } },
                React.createElement('span', { style: { fontFamily: FONT.mono } }, op.source),
                React.createElement('span', { style: { width: 3, height: 3, borderRadius: 3, background: t.text.disabled, display: 'inline-block' } }),
                React.createElement('span', null, op.posted)
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('button', {
                    onClick: (e) => { e.stopPropagation(); s.toggleSave(op.id); },
                    title: saved ? 'Saved' : 'Save',
                    style: { ...iconBtn(t), color: saved ? OC.orange : t.text.secondary, borderColor: saved ? OC.orange : t.border.base, background: saved ? (s.theme === 'dark' ? 'rgba(249,115,22,.12)' : OC.orangeMuted) : 'transparent' }
                }, React.createElement(Icon, { name: saved ? 'bookmarkFilled' : 'bookmark', size: 15 })),
                React.createElement('button', {
                    onClick: (e) => { e.stopPropagation(); s.startApply(op.id); },
                    style: { ...iconBtn(t), background: applied ? (s.theme === 'dark' ? t.bg.subtle : '#F0FDF4') : OC.orange, color: applied ? OC.success : '#fff', border: applied ? `1px solid ${OC.success}` : 'none', padding: '0 14px', width: 'auto', fontWeight: 600, fontSize: 13, gap: 5 }
                }, applied ? React.createElement(React.Fragment, null, React.createElement(Icon, { name: 'check', size: 13 }), ' Applied') : React.createElement(React.Fragment, null, 'Apply ', React.createElement(Icon, { name: 'arrowRight', size: 13 })))
            )
        )
    );
}

// --- OpportunityDetailsSection ---
function OpportunityDetailsSection({ op, theme }) {
    const t = useTheme(theme);
    if (!op || !op.detailFields || !op.detailFields.length) return null;

    return React.createElement('div', { style: { marginBottom: 24 } },
        React.createElement('h2', {
            style: {
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: 18,
                color: t.text.primary,
                margin: '0 0 14px',
            }
        }, 'Opportunity details'),
        React.createElement('div', {
            style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
            }
        },
            op.detailFields.map((field) =>
                React.createElement('div', {
                    key: field.label,
                    style: {
                        background: t.bg.subtle,
                        border: `1px solid ${t.border.base}`,
                        borderRadius: 12,
                        padding: '12px 14px',
                    }
                },
                    React.createElement('div', {
                        style: {
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: t.text.secondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            marginBottom: 6,
                        }
                    }, field.label),
                    React.createElement('div', {
                        style: { fontSize: 14.5, color: t.text.primary, lineHeight: 1.45, whiteSpace: 'pre-wrap' }
                    }, field.value)
                )
            )
        )
    );
}


function DetailScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const id = s.route.id;
    const [op, setOp] = useState(() => s.findOpportunity(id));
    const [loading, setLoading] = useState(!s.findOpportunity(id));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const cached = s.findOpportunity(id);
            if (cached) {
                setOp(cached);
                setLoading(false);
                return;
            }
            setLoading(true);
            const fetched = await s.fetchOpportunity(id);
            if (!cancelled) {
                setOp(fetched);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    if (loading) {
        return React.createElement('div', { style: { maxWidth: 920, margin: '0 auto', padding: '80px 32px', textAlign: 'center', color: t.text.secondary } }, 'Loading opportunity…');
    }

    if (!op) {
        return React.createElement('div', { style: { maxWidth: 920, margin: '0 auto', padding: '80px 32px', textAlign: 'center' } },
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 24, color: t.text.primary, margin: '0 0 10px' } }, 'Opportunity not found'),
            React.createElement('p', { style: { fontSize: 14.5, color: t.text.secondary, margin: '0 0 20px' } }, "This listing may have expired or the link is incorrect."),
            React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: () => s.go('feed') }, 'Back to opportunities')
        );
    }

    const cat = CATEGORY[op.category] || CATEGORY.jobs;
    const saved = s.isSaved(op.id);
    const applied = s.hasApplied(op.id);

    return React.createElement('div', { style: { maxWidth: 860, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '36px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text.secondary, marginBottom: 20 } },
                React.createElement('button', { onClick: () => s.go('home'), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, fontSize: 12.5, padding: 0 } }, 'Home'),
                React.createElement(Icon, { name: 'chevRight', size: 12 }),
                React.createElement('button', { onClick: () => s.go('feed'), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, fontSize: 12.5, padding: 0 } }, 'Opportunities'),
                React.createElement(Icon, { name: 'chevRight', size: 12 }),
                React.createElement('span', { style: { color: t.text.primary, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 } }, op.title)
            ),
            React.createElement(Card, { theme: s.theme, padding: 28 },
                op.isExpired ? React.createElement('div', {
                    style: {
                        background: `${OC.neutral}18`,
                        border: `1px solid ${t.border.base}`,
                        borderRadius: 12,
                        padding: '12px 14px',
                        marginBottom: 16,
                        color: t.text.secondary,
                        fontSize: 14,
                    }
                }, 'This listing has expired and is no longer accepting applications.') : null,
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18, flexWrap: 'wrap' } },
                    React.createElement(Badge, { color: cat.color, theme: s.theme, icon: React.createElement(Icon, { name: cat.icon, size: 12 }) }, cat.label),
                    React.createElement(Deadline, { days: op.daysLeft, theme: s.theme, size: 'md' })
                ),
                React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 18 } },
                    React.createElement(Avatar, { name: op.org, size: 52, theme: s.theme }),
                    React.createElement('div', null,
                        React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 26, margin: 0, color: t.text.primary, letterSpacing: '-0.02em' } }, op.title),
                        React.createElement('div', { style: { fontSize: 15, color: t.text.secondary, marginTop: 6, fontWeight: 500 } }, op.org)
                    )
                ),
                React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 } },
                    React.createElement(MetaChip, { theme: s.theme, icon: 'mapPin' }, op.location),
                    op.salary && op.salary !== '—' ? React.createElement(MetaChip, { theme: s.theme, icon: 'sparkle' }, op.salary) : null,
                    op.type ? React.createElement(MetaChip, { theme: s.theme, icon: 'briefcase' }, op.type) : null,
                    op.level ? React.createElement(MetaChip, { theme: s.theme, icon: 'award' }, op.level) : null
                ),
                op.summary ? React.createElement('p', { style: { fontSize: 15, lineHeight: 1.6, color: t.text.primary, margin: '0 0 20px' } }, op.summary) : null,
                React.createElement(OpportunityDetailsSection, { op, theme: s.theme }),
                op.skills && op.skills.length ? React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 } },
                    op.skills.map(sk => React.createElement(MonoTag, { key: sk, theme: s.theme }, sk))
                ) : null,
                React.createElement('div', { style: { display: 'flex', gap: 10, paddingTop: 20, borderTop: `1px solid ${t.border.base}` } },
                    React.createElement(Button, {
                        kind: 'primary', size: 'lg', theme: s.theme, style: { flex: 1 },
                        onClick: () => s.startApply(op.id),
                        icon: applied ? React.createElement(Icon, { name: 'check', size: 16 }) : undefined,
                        iconRight: !applied ? React.createElement(Icon, { name: 'arrowUpRight', size: 16 }) : undefined,
                    }, applied ? 'Applied' : 'Apply now'),
                    React.createElement(Button, {
                        kind: 'secondary', size: 'lg', theme: s.theme,
                        onClick: () => s.toggleSave(op.id),
                        icon: React.createElement(Icon, { name: saved ? 'bookmarkFilled' : 'bookmark', size: 16, color: saved ? OC.orange : undefined })
                    }, saved ? 'Saved' : 'Save')
                )
            )
        )
    );
}


export {
  HomeScreen, FeedScreen, FeedRow, OpportunityCard, OpportunityDetailsSection, DetailScreen,
};
